import { CombinedStateCreator } from "../store";
import type { SessionSection } from "@/lib/utils/workoutSectionClock";
import { useSectionClockStore } from "./sectionClocks";

export interface PreviousLoggedSet {
  set_num?: number;
  reps?: number;
  weight?: number;
  volume?: number;
  time_seconds?: number;
  distance_meters?: number;
}

export interface WorkoutSet {
  id?: string;
  startRange?: number;
  endRange?: number;
  targetWeight?: number;
  reps?: number | null;
  weight?: number | null;
  targetTimeSeconds?: number;
  targetDistanceMeters?: number;
  time_seconds?: number | null;
  distance_meters?: number | null;
  set_type?: string | null;
}

export interface WorkoutExercise {
  id: string;
  workout_exercise_id?: string | null;
  section_id?: string | null;
  ex_order: number;
  sub_order: number;
  name: string;
  tracking_type?: string | null;
  tempo?: string | null;
  rest_time?: number | null;
  rir?: number | null;
  coach_note?: string | null;
  instructions?: string[] | null;
  primary_muscles?: string[] | null;
  equipment?: string | null;
  experience?: string | null;
  sets: WorkoutSet[];
  notes?: string | null;
  previousSets?: PreviousLoggedSet[];
}

export interface ActiveWorkoutData {
  clientId: string;
  clientName?: string;
  workoutId?: string;
  logId?: string;
  name: string;
  planName?: string;
  description?: string | null;
  logNotes?: string;
  exercises: WorkoutExercise[][];
  sections?: SessionSection[];
  startTime: Date;
  endTime?: Date;
  returnTo?: string;
}

export interface RestTimerEntry {
  remaining: number;
  running: boolean;
  duration?: number;
  exerciseName?: string;
  lastTickAt?: number;
}

export interface WorkoutSessionStore {
  workoutSession: {
    activeWorkoutData?: ActiveWorkoutData;
    minimized: boolean;
    restDefaultSeconds: number;
    autoStartRestTimer: boolean;
    restTimer: RestTimerEntry | null;
    startWorkoutSession: (data: ActiveWorkoutData) => void;
    updateActiveWorkout: (data: ActiveWorkoutData) => void;
    setMinimized: (minimized: boolean) => void;
    setRestDefaultSeconds: (seconds: number) => void;
    setAutoStartRestTimer: (enabled: boolean) => void;
    setRestTimer: (
      updater:
        | RestTimerEntry
        | null
        | ((prev: RestTimerEntry | null) => RestTimerEntry | null)
    ) => void;
    stop: () => void;
  };
}

export const createWorkoutStore: CombinedStateCreator<WorkoutSessionStore> = (
  set
) => ({
  workoutSession: {
    activeWorkoutData: undefined,
    minimized: false,
    restDefaultSeconds: 90,
    autoStartRestTimer: true,
    restTimer: null,
    startWorkoutSession: (data: ActiveWorkoutData) => {
      useSectionClockStore.getState().resetSectionClocks();
      set((state: WorkoutSessionStore) => {
        state.workoutSession.activeWorkoutData = data;
        state.workoutSession.minimized = false;
        state.workoutSession.restTimer = null;
      });
    },
    updateActiveWorkout: (data: ActiveWorkoutData) => {
      set((state: WorkoutSessionStore) => {
        state.workoutSession.activeWorkoutData = data;
      });
    },
    setMinimized: (minimized: boolean) => {
      set((state: WorkoutSessionStore) => {
        state.workoutSession.minimized = minimized;
      });
    },
    setRestDefaultSeconds: (seconds: number) => {
      set((state: WorkoutSessionStore) => {
        state.workoutSession.restDefaultSeconds = Math.max(0, seconds);
      });
    },
    setAutoStartRestTimer: (enabled: boolean) => {
      set((state: WorkoutSessionStore) => {
        state.workoutSession.autoStartRestTimer = enabled;
      });
    },
    setRestTimer: (updater) => {
      set((state: WorkoutSessionStore) => {
        state.workoutSession.restTimer =
          typeof updater === "function"
            ? updater(state.workoutSession.restTimer ?? null)
            : updater;
      });
    },
    stop: () => {
      useSectionClockStore.getState().resetSectionClocks();
      set((state: WorkoutSessionStore) => {
        state.workoutSession.activeWorkoutData = undefined;
        state.workoutSession.minimized = false;
        state.workoutSession.restTimer = null;
      });
    },
  },
});
